import { SubscriptionStatus, BillingPortalUrl } from "../types";
import apiClient from "./apiClient";
import { getSupportEmail } from "../utils/domainConfig";
import { TRIAL_PURCHASE_ADMIN_ONLY_MESSAGE } from "../utils/trialPurchase";

/**
 * Subscription Plan Types
 */
export interface Brand {
  id: string;
  slug: string;
  displayName: string;
}

export interface SubscriptionPlan {
  id: string;
  name: string;
  stripeProductId: string;
  description: string;
  status: string;
  includedModules: string[];
  brands: Brand[];
  prices?: SubscriptionPrice[];
  createdAt: string;
  updatedAt: string;
}

export interface SubscriptionPrice {
  id: string;
  stripePriceId: string;
  planId: string;
  amount: number;
  currency: string;
  billingInterval: string;
  /** Human-readable label from backend, e.g. "miesięcznie", "kwartalnie", "rocznie" */
  billingIntervalLabel?: string;
  intervalCount: number;
  isActive: boolean;
}

export interface SyncStripeProductsResponse {
  success: boolean;
  productsProcessed: number;
  pricesProcessed: number;
  message: string;
}

export interface SyncDetail {
  stripeSubscriptionId: string;
  stripeCustomerId: string | null;
  customerEmail: string;
  status: "success" | "skipped" | "failed";
  reason?: string;
  tenantId?: string;
  tenantName?: string;
  planName?: string;
}

export interface SyncStripeSubscriptionsResponse {
  success: boolean;
  totalStripeSubscriptions: number;
  successfulSyncs: number;
  skipped: number;
  failures: number;
  details: SyncDetail[];
}

/**
 * Get current user's subscription status
 * Returns complete subscription details including plan, status, dates, and enabled modules
 *
 * @returns SubscriptionStatus object or null if no subscription exists
 * @throws Error if request fails or user is not authenticated
 */
export async function getCurrentSubscription(): Promise<SubscriptionStatus | null> {
  try {
    const response = await apiClient.get<SubscriptionStatus>(
      "/account/subscription"
    );
    return response.data;
  } catch (error: any) {
    if (error.response?.status === 404) {
      // 404 means no subscription exists - this is a valid state, not an error
      return null;
    }
    throw new Error(
      `Błąd pobierania subskrypcji: ${error.response?.statusText || error.message}`
    );
  }
}

/**
 * Sync Stripe Products to Database
 * Admin-only endpoint to synchronize Stripe products and prices
 *
 * @returns SyncStripeProductsResponse with operation statistics
 * @throws Error if request fails or user is not ADMIN
 */
export async function syncStripeProducts(): Promise<SyncStripeProductsResponse> {
  try {
    const response = await apiClient.post<SyncStripeProductsResponse>(
      "/account/sync-stripe-products"
    );
    return response.data;
  } catch (error: any) {
    if (error.response?.status === 401 || error.response?.status === 403) {
      throw new Error(
        "Brak uprawnień. Tylko administratorzy mogą synchronizować plany."
      );
    }
    throw new Error(
      `Błąd synchronizacji planów: ${error.response?.statusText || error.message}`
    );
  }
}

/**
 * Get all subscription plans (Admin Only)
 * Returns all active subscription plans including those without configured modules
 *
 * This endpoint is for admin UI - shows ALL plans (even without modules)
 * so administrators can configure newly synced plans.
 *
 * @returns Array of SubscriptionPlan objects
 * @throws Error if request fails or user is not ADMIN
 */
export async function getSubscriptionPlans(): Promise<SubscriptionPlan[]> {
  try {
    const response = await apiClient.get<SubscriptionPlan[]>(
      "/account/subscription-plans"
    );
    return response.data;
  } catch (error: any) {
    if (error.response?.status === 401 || error.response?.status === 403) {
      throw new Error(
        "Brak uprawnień. Tylko administratorzy mogą przeglądać wszystkie plany."
      );
    }
    throw new Error(
      `Błąd pobierania planów: ${error.response?.statusText || error.message}`
    );
  }
}

/**
 * Update subscription plan modules
 * Admin-only endpoint to configure which modules are included in a plan
 *
 * @param planId - UUID of the subscription plan
 * @param modules - Array of module identifiers (e.g., ['hotel:*', 'grants:schedule'])
 * @returns Updated SubscriptionPlan object
 * @throws Error if request fails or user is not ADMIN
 */
export async function updateSubscriptionPlanModules(
  planId: string,
  modules: string[]
): Promise<SubscriptionPlan> {
  try {
    const response = await apiClient.patch<SubscriptionPlan>(
      `/account/subscription-plans/${planId}/modules`,
      { includedModules: modules }
    );
    return response.data;
  } catch (error: any) {
    if (error.response?.status === 401 || error.response?.status === 403) {
      throw new Error(
        "Brak uprawnień. Tylko administratorzy mogą edytować plany."
      );
    }
    if (error.response?.status === 404) {
      throw new Error("Plan subskrypcji nie został znaleziony");
    }
    throw new Error(
      `Błąd aktualizacji modułów: ${error.response?.statusText || error.message}`
    );
  }
}

/**
 * Sync Stripe Subscriptions to Database
 * Admin-only endpoint to synchronize existing Stripe subscriptions to local database
 *
 * @returns SyncStripeSubscriptionsResponse with detailed operation statistics
 * @throws Error if request fails or user is not ADMIN
 */
export async function syncStripeSubscriptions(): Promise<SyncStripeSubscriptionsResponse> {
  try {
    const response = await apiClient.post<SyncStripeSubscriptionsResponse>(
      "/account/sync-stripe-subscriptions"
    );
    return response.data;
  } catch (error: any) {
    if (error.response?.status === 401 || error.response?.status === 403) {
      throw new Error(
        "Brak uprawnień. Tylko administratorzy mogą synchronizować subskrypcje."
      );
    }
    throw new Error(
      `Błąd synchronizacji subskrypcji: ${error.response?.statusText || error.message}`
    );
  }
}

/**
 * Update subscription plan brands
 * Admin-only endpoint to configure which brands can offer a plan
 *
 * @param planId - UUID of the subscription plan
 * @param brandIds - Array of brand UUIDs to assign to the plan
 * @returns Updated SubscriptionPlan object
 * @throws Error if request fails or user is not ADMIN
 */
export async function updateSubscriptionPlanBrands(
  planId: string,
  brandIds: string[]
): Promise<SubscriptionPlan> {
  try {
    const response = await apiClient.patch<SubscriptionPlan>(
      `/account/subscription-plans/${planId}/brands`,
      { brandIds }
    );
    return response.data;
  } catch (error: any) {
    if (error.response?.status === 401 || error.response?.status === 403) {
      throw new Error(
        "Brak uprawnień. Tylko administratorzy mogą edytować plany."
      );
    }
    if (error.response?.status === 404) {
      throw new Error("Plan subskrypcji nie został znaleziony");
    }
    throw new Error(
      `Błąd aktualizacji brandów: ${error.response?.statusText || error.message}`
    );
  }
}

/**
 * Get all brands
 * Returns all brands from the system
 *
 * @returns Array of Brand objects
 * @throws Error if request fails or user is not authenticated
 */
export async function getAllBrands(): Promise<Brand[]> {
  try {
    const response = await apiClient.get<Brand[]>("/brands");
    return response.data;
  } catch (error: any) {
    throw new Error(
      `Błąd pobierania brandów: ${error.response?.statusText || error.message}`
    );
  }
}

/**
 * Get available subscription plans for current tenant's brand
 * Returns plans filtered by tenant's brand (many-to-many relationship)
 *
 * @returns Array of SubscriptionPlan objects with prices
 * @throws Error if request fails or user is not authenticated
 */
export async function getAvailablePlans(): Promise<SubscriptionPlan[]> {
  try {
    const response = await apiClient.get<SubscriptionPlan[]>(
      "/account/subscription/available-plans"
    );
    return response.data;
  } catch (error: any) {
    throw new Error(
      `Błąd pobierania dostępnych planów: ${error.response?.statusText || error.message}`
    );
  }
}

/**
 * Check trial eligibility for current authenticated user
 * Returns trial eligibility information based on user's module overlap
 *
 * @returns Trial eligibility object with eligible flag, days, and reason
 * @throws Error if request fails or user is not authenticated
 */
export async function checkTrialEligibility(): Promise<{
  eligible: boolean;
  trialDays: number;
  reason: string;
}> {
  try {
    const response = await apiClient.get<{
      eligible: boolean;
      trialDays: number;
      reason: string;
    }>("/account/subscription/trial-eligibility");
    return response.data;
  } catch (error: any) {
    throw new Error(
      `Błąd sprawdzania uprawnień do okresu próbnego: ${error.response?.statusText || error.message}`
    );
  }
}

/**
 * Default fallback path for the self-service resubscribe flow.
 * Used when backend-provided redirectPath fails same-origin validation.
 */
const RESUBSCRIBE_FALLBACK_PATH = "/billing/self-service/resubscribe";

/**
 * Abstract base class for typed 409 Conflict errors returned by the account service.
 * Concrete subclasses must declare a stable `code` discriminator for callers to switch on.
 */
export abstract class AccountServiceConflictError extends Error {
  abstract readonly code: string;
  readonly status = 409;
}

/**
 * Typed error thrown when POST /account/subscription/activate returns 409 Conflict
 * with code='USE_NEW_BILLING'. Indicates the tenant has a record in billing.subscriptions
 * (new Ordering BC) and must use the self-service resubscribe flow instead of the legacy endpoint.
 */
export class UseNewBillingError extends AccountServiceConflictError {
  readonly code = "USE_NEW_BILLING";
  readonly redirectPath: string;
  constructor(redirectPath: string) {
    super("Twoje konto używa nowego billingu — przekierowuję do odnowienia subskrypcji.");
    this.name = "UseNewBillingError";
    // Same-origin path only — reject absolute URLs (http://, https://) and protocol-relative (//host)
    // to prevent open redirect via compromised backend / MITM. Regex matches "/x..." but not "//x".
    this.redirectPath = /^\/[^/]/.test(redirectPath) ? redirectPath : RESUBSCRIBE_FALLBACK_PATH;
  }
}

/**
 * Activate subscription for current tenant
 * Creates Stripe Checkout session and returns URL for payment
 *
 * @param dto - Activation data with planId and stripePriceId
 * @returns Checkout URL object for Stripe payment redirect
 * @throws UseNewBillingError if tenant has new billing (409 USE_NEW_BILLING)
 * @throws Error if request fails, plan not available, or user is not authenticated
 */
export async function activateSubscription(dto: {
  planId: string;
  stripePriceId: string;
}): Promise<{ checkoutUrl: string }> {
  try {
    const response = await apiClient.post<{ checkoutUrl: string }>(
      "/account/subscription/activate",
      dto
    );
    return response.data;
  } catch (error: any) {
    if (
      error.response?.status === 409 &&
      error.response?.data?.code === "USE_NEW_BILLING"
    ) {
      // Constructor enforces same-origin validation and falls back to the safe default
      // when the backend-provided value fails the check (defense-in-depth vs open redirect).
      throw new UseNewBillingError(
        error.response?.data?.redirectPath ?? RESUBSCRIBE_FALLBACK_PATH
      );
    }
    if (error.response?.status === 400) {
      throw new Error(
        "Plan nie jest dostępny dla twojego brandu lub nieprawidłowe dane"
      );
    }
    if (error.response?.status === 404) {
      throw new Error("Nie znaleziono planu subskrypcji lub tenanta");
    }
    throw new Error(
      `Błąd aktywacji subskrypcji: ${error.response?.statusText || error.message}`
    );
  }
}

/**
 * Renewal response from POST /account/subscription/renew
 */
export interface RenewSubscriptionResponse {
  proformaId: string;
  billingPeriodStart: string;
  billingPeriodEnd: string;
  amountInCents: number;
  previousProformaCanceled: boolean;
}

/**
 * Renew subscription with a new billing period (self-service, bank_transfer only)
 * Creates a proforma invoice for the next billing period.
 * If a pending proforma already exists, it is canceled and replaced.
 *
 * @param dto - Renewal data with planId and stripePriceId
 * @returns RenewSubscriptionResponse with proforma details
 * @throws Error if request fails, subscription not eligible, or rate limited
 */
export async function renewSubscription(dto: {
  planId: string;
  stripePriceId: string;
}): Promise<RenewSubscriptionResponse> {
  try {
    const response = await apiClient.post<RenewSubscriptionResponse>(
      "/account/subscription/renew",
      dto
    );
    return response.data;
  } catch (error: any) {
    if (error.response?.status === 404) {
      throw new Error("Nie masz aktywnej subskrypcji");
    }
    if (error.response?.status === 400) {
      throw new Error(
        error.response?.data?.message || "Subskrypcja nie kwalifikuje się do przedłużenia"
      );
    }
    if (error.response?.status === 429) {
      throw new Error("Zbyt wiele prób, spróbuj za minutę");
    }
    throw new Error(
      `Błąd przedłużania subskrypcji: ${error.response?.statusText || error.message}`
    );
  }
}

/**
 * Renewal preview response from GET /billing/self-service/renewal-preview
 */
export interface RenewalPreviewPrice {
  netCents: number;
  grossCents: number;
  currency: string;
}

export interface RenewalPreviewProduct {
  id: string;
  name: string;
}

export type BillingInterval = "monthly" | "quarterly" | "yearly";

export interface RenewalPreviewResponse {
  trialEndsAt: string | null;
  renewalProduct: RenewalPreviewProduct;
  billingInterval: BillingInterval;
  /** Calculated price for the selected billing interval (AC-15: renamed from estimatedPrice) */
  price: RenewalPreviewPrice;
  currentPeriodEnd: string | null;
  proformaExpectedAt: string;
  canChangeBillingInterval: boolean;
  subscriptionStatus: string;
  /** True when a DRAFT self-service order already exists for this tenant (AC-13/AC-14) */
  hasExistingOrder?: boolean;
  /** The billing interval of the existing DRAFT order, if any (AC-14) */
  currentOrderInterval?: BillingInterval;
  /** Proforma document number (e.g. "P17/05/2026") or order number fallback for existing order (AC-4) */
  currentOrderNumber?: string | null;
  /** False when the next-period package change is switched off (AC-16 feature flags) */
  renewalPreferenceAvailable?: boolean;
}

/**
 * Update billing preference response from PATCH /billing/self-service/billing-preference
 */
export interface UpdateBillingPreferenceResponse {
  billingInterval: BillingInterval;
  proformaCanceled: boolean;
  newOrderCreated: boolean;
  /** URL for downloading the generated proforma PDF (AC-16) */
  proformaPdfUrl?: string;
}

/**
 * Get renewal preview for current tenant's subscription
 * Returns renewal context including billing interval, price, and proforma timing.
 * Pure read operation — no side effects (AC-12).
 *
 * @param billingInterval - Optional interval to calculate price for (AC-12)
 * @returns RenewalPreviewResponse with renewal details
 * @throws Error if request fails or user is not authenticated
 */
export async function getRenewalPreview(
  billingInterval?: BillingInterval
): Promise<RenewalPreviewResponse> {
  try {
    const params = billingInterval ? { billingInterval } : undefined;
    const response = await apiClient.get<RenewalPreviewResponse>(
      "/billing/self-service/renewal-preview",
      { params }
    );
    return response.data;
  } catch (error: any) {
    if (error.response?.status === 404) {
      throw new Error("Brak aktywnej subskrypcji");
    }
    if (error.response?.status === 503) {
      throw new Error("Nie udało się wyliczyć ceny. Spróbuj ponownie.");
    }
    throw new Error(
      `Błąd pobierania podglądu odnowienia: ${error.response?.statusText || error.message}`
    );
  }
}

/**
 * Update billing interval preference for current tenant
 * If a pending proforma exists, it will be canceled and a new order created
 *
 * @param billingInterval - New billing interval (monthly/quarterly/yearly)
 * @returns UpdateBillingPreferenceResponse with updated interval info
 * @throws Error if request fails or user is not authenticated
 */
export async function updateBillingPreference(
  billingInterval: BillingInterval
): Promise<UpdateBillingPreferenceResponse> {
  try {
    const response = await apiClient.patch<UpdateBillingPreferenceResponse>(
      "/billing/self-service/billing-preference",
      { billingInterval }
    );
    return response.data;
  } catch (error: any) {
    if (error.response?.status === 400) {
      throw new Error(
        error.response?.data?.message || "Nieprawidłowy interwał rozliczeniowy"
      );
    }
    if (error.response?.status === 404) {
      throw new Error("Brak aktywnej subskrypcji");
    }
    if (error.response?.status === 503) {
      throw new Error("Nie udało się wyliczyć ceny. Spróbuj ponownie.");
    }
    throw new Error(
      `Błąd aktualizacji preferencji rozliczeniowych: ${error.response?.statusText || error.message}`
    );
  }
}

/**
 * Catalog entry from GET /billing/catalog
 */
export interface CatalogEntry {
  id: string;
  productId: string;
  displayName: string;
  marketingDescription?: string | null;
  categories?: string[];
  metadata?: Record<string, unknown> | null;
  sortOrder: number;
  product?: {
    id: string;
    name: string;
    productType: string;
  };
}

export interface CatalogResponse {
  data: CatalogEntry[];
  total: number;
}

/**
 * Get catalog entries for a brand
 * Returns visible catalog entries filtered by brand ID
 *
 * @param brandId - UUID of the brand to filter catalog entries
 * @returns CatalogResponse with data array and total count
 * @throws Error if request fails
 */
export async function getCatalogEntries(brandId: string): Promise<CatalogResponse> {
  try {
    const response = await apiClient.get<CatalogResponse>(
      `/billing/catalog?brandId=${encodeURIComponent(brandId)}`
    );
    return response.data;
  } catch (error: any) {
    throw new Error(
      `Błąd pobierania katalogu: ${error.response?.statusText || error.message}`
    );
  }
}

/**
 * Resubscribe with a specific product (for canceled/expired ordering-model subscriptions).
 * HTTP 201 → new order created (wasCreated: true).
 * HTTP 200 → existing order returned unchanged, same interval (wasCreated: false).
 *
 * @param data - Object with optional productId and billingInterval
 * @returns ResubscribeResponse with orderId, proformaPdfUrl and wasCreated flag
 * @throws ResubscribeConflictError on 409 (errorCode may indicate PAYMENT_RECEIVED_CONTACT_SUPPORT)
 * @throws Error if request fails
 */
export async function resubscribeWithProduct(data: {
  productId?: string;
  billingInterval?: BillingInterval;
}): Promise<ResubscribeResponse> {
  try {
    const response = await apiClient.post<ResubscribeResponse>(
      "/billing/self-service/resubscribe",
      data
    );
    return response.data;
  } catch (error: any) {
    if (error.response?.status === 409) {
      throw new ResubscribeConflictError(
        error.response?.data?.proformaPdfUrl ?? null,
        error.response?.data?.errorCode ?? null,
        error.response?.data?.orderNumber ?? null
      );
    }
    throw error;
  }
}

/**
 * Resubscribe response from POST /billing/self-service/resubscribe.
 * HTTP 201 means a new order was created (replace or fresh).
 * HTTP 200 means the existing order was returned unchanged (idempotency, same interval).
 */
export interface ResubscribeResponse {
  orderId: string;
  proformaPdfUrl: string | null;
  /** True when a new order was created (HTTP 201); false for idempotent same-interval response (HTTP 200). */
  wasCreated: boolean;
}

/**
 * Typed error thrown when POST /billing/self-service/resubscribe returns 409 Conflict.
 * errorCode='PAYMENT_RECEIVED_CONTACT_SUPPORT' → registered payment on existing order (AC-6).
 * errorCode=null → fallback conflict (old behaviour before deploy of replace flow).
 */
export class ResubscribeConflictError extends AccountServiceConflictError {
  readonly code = "RESUBSCRIBE_CONFLICT";
  constructor(
    readonly proformaPdfUrl: string | null,
    /** Backend errorCode from response body (AC-6). Null if not provided. */
    readonly errorCode: string | null = null,
    /** Order number of the conflicting existing order (AC-6, used for mailto body). */
    readonly orderNumber: string | null = null
  ) {
    super("Masz już oczekujące zamówienie odnowienia");
    this.name = "ResubscribeConflictError";
  }
}

/**
 * Resubscribe an expired tenant subscription via bank transfer
 * Creates a new order for the expired tenant with the specified billing interval.
 * Returns 409 Conflict if a pending renewal order already exists.
 *
 * @param billingInterval - Optional billing interval (monthly/quarterly/yearly).
 *   When undefined, the backend uses the last known billing interval of the expired subscription.
 * @returns ResubscribeResponse with orderId and optional proforma PDF URL
 * @throws ResubscribeConflictError if a pending renewal order already exists (409)
 * @throws Error if request fails or subscription is not in expired state
 */
export async function resubscribe(
  billingInterval?: BillingInterval
): Promise<ResubscribeResponse> {
  try {
    const response = await apiClient.post<ResubscribeResponse>(
      "/billing/self-service/resubscribe",
      { billingInterval }
    );
    return response.data;
  } catch (error: any) {
    if (error.response?.status === 409) {
      throw new ResubscribeConflictError(
        error.response?.data?.proformaPdfUrl ?? null,
        error.response?.data?.errorCode ?? null,
        error.response?.data?.orderNumber ?? null
      );
    }
    if (error.response?.status === 404) {
      throw new Error("Nie znaleziono subskrypcji");
    }
    if (error.response?.status === 400) {
      throw new Error(
        error.response?.data?.message || "Nieprawidłowe dane zamówienia"
      );
    }
    throw new Error(
      `Błąd wznawiania subskrypcji: ${error.response?.statusText || error.message}`
    );
  }
}

/** Response of POST /billing/self-service/trial-purchase (5c). */
export interface TrialPurchaseResponse {
  billingInterval: BillingInterval;
  /** False when an open order with the same interval already existed (idempotent retry). */
  newOrderCreated: boolean;
  proformaPdfUrl: string | null;
  autoRenewal: boolean;
}

/**
 * 5c: "Kupuję — wystaw proformę" during a trial (trial invite without
 * proforma). Issues the purchase proforma and turns auto-renewal on.
 *
 * Errors are mapped by status: 5xx bodies carry the generic English message
 * of AllExceptionsFilter and 403/404 bodies are English too, so only 400/409/422
 * pass the backend's (Polish) domain message through.
 *
 * @param billingInterval - Optional interval (default: current trial preference)
 * @param supportEmail - Address shown when issuing the proforma failed (default: brand of the domain)
 * @throws Error with a user-facing message on failure
 */
export async function purchaseTrial(
  billingInterval?: BillingInterval,
  supportEmail: string = getSupportEmail()
): Promise<TrialPurchaseResponse> {
  try {
    const response = await apiClient.post<TrialPurchaseResponse>(
      "/billing/self-service/trial-purchase",
      billingInterval ? { billingInterval } : {}
    );
    return response.data;
  } catch (error: unknown) {
    const axiosError = error as {
      response?: { status?: number; data?: { message?: string } };
    };
    const status = axiosError.response?.status;
    if (status !== undefined && status >= 500) {
      throw new Error(
        "Nie udało się wystawić proformy. Spróbuj ponownie za chwilę. " +
          `Jeśli problem się powtarza, napisz na ${supportEmail}.`
      );
    }
    if (status === 429) {
      throw new Error("Zbyt wiele prób, spróbuj za chwilę");
    }
    if (status === 404) {
      throw new Error("Brak aktywnej subskrypcji");
    }
    if (status === 403) {
      throw new Error(TRIAL_PURCHASE_ADMIN_ONLY_MESSAGE);
    }
    throw new Error(
      axiosError.response?.data?.message ||
        "Nie udało się złożyć zamówienia. Spróbuj ponownie."
    );
  }
}

/**
 * Update auto-renewal preference for current tenant's subscription (AC-5)
 * Calls PATCH /billing/self-service/auto-renewal with { autoRenewal }.
 * Lookup is performed exclusively via req.tenantId from JWT (not subscriptionId in body).
 *
 * @param autoRenewal - New value for the auto-renewal flag
 * @returns Object with updated autoRenewal value
 * @throws Error if request fails, subscription is in terminal state (409), or user is not tenant admin
 */
export async function updateAutoRenewal(
  autoRenewal: boolean
): Promise<{ autoRenewal: boolean }> {
  try {
    const response = await apiClient.patch<{ autoRenewal: boolean }>(
      "/billing/self-service/auto-renewal",
      { autoRenewal }
    );
    return response.data;
  } catch (error: unknown) {
    const axiosError = error as { response?: { status?: number; data?: { message?: string }; statusText?: string } };
    if (axiosError.response?.status === 409) {
      throw new Error(
        axiosError.response?.data?.message ||
          "Nie można zmienić ustawienia dla subskrypcji w stanie końcowym"
      );
    }
    if (axiosError.response?.status === 404) {
      throw new Error("Nie znaleziono aktywnej subskrypcji");
    }
    if (axiosError.response?.status === 429) {
      throw new Error("Zbyt wiele prób, spróbuj za chwilę");
    }
    throw new Error("Błąd aktualizacji automatycznego odnawiania");
  }
}

/**
 * Get Stripe Customer Portal URL
 * Creates a billing portal session and returns the URL for subscription management
 *
 * @returns BillingPortalUrl object with Stripe portal session URL
 * @throws Error if request fails or user is not authenticated
 */
export async function getBillingPortalUrl(): Promise<BillingPortalUrl> {
  try {
    const response = await apiClient.get<BillingPortalUrl>(
      "/account/billing-portal"
    );
    return response.data;
  } catch (error: any) {
    throw new Error(
      `Błąd tworzenia linku do portalu: ${error.response?.statusText || error.message}`
    );
  }
}

// ============================================================
// AC-11/AC-14/AC-15: Renewal preference (package on next period) — SubscriptionTimeline
// ============================================================

/** Where the renewal target on `RenewalTimelinePreview` came from (AC-3 `RenewalTargetPolicy`). */
export type RenewalSource = "own" | "upgrades_to" | "preference" | "own_fallback";

/**
 * Closed list of reasons a renewal price could not be calculated (AC-10, review L-7) —
 * machine codes, never shown raw; the UI says "cena chwilowo niedostępna".
 */
export type PriceUnavailableReason = "PRICE_NOT_CONFIGURED" | "PRICE_CALCULATION_FAILED";

/** Closed list of reasons a renewal-preference change can be blocked (AC-5). */
export type PreferenceBlockedReason =
  | "SCHEDULED_SUCCESSOR_EXISTS"
  | "RENEWAL_PAYMENT_RECEIVED"
  | "MANUAL_ORDER_OPEN"
  | "NOT_ACTIVE"
  | "AUTO_RENEWAL_DISABLED";

/**
 * Enriched renewal preview for `SubscriptionTimeline` (AC-11).
 * Expected contract for `GET /billing/self-service/renewal-preview` once the backend lands
 * AC-3/AC-8/AC-10: existing fields (`renewalProduct`, `billingInterval`, `price`, ...) plus the
 * additions below. `currentProduct`/`currentBillingInterval` describe the presently ACTIVE
 * package (the "dziś→Y" segment) — distinct from `renewalProduct`/`billingInterval`, which
 * describe the resolved target for the next period (the "Y→Z" segment).
 */
export interface RenewalTimelinePreview {
  currentProduct: { id: string; name: string };
  currentBillingInterval: BillingInterval;
  /** Y — end of the current period. */
  currentPeriodEnd: string;
  renewalProduct: { id: string; name: string };
  billingInterval: BillingInterval;
  /** AC-10: null (never 0) when pricing failed — paired with `priceUnavailableReason`. */
  price: RenewalPreviewPrice | null;
  priceUnavailableReason?: PriceUnavailableReason | null;
  /** Z — end of the next period, once renewed. */
  nextPeriodEnd: string | null;
  renewalSource: RenewalSource;
  scheduledSuccessor?: {
    productName: string;
    billingInterval: BillingInterval;
    periodEnd: string;
  } | null;
  renewalProforma?: { number: string; dueDate: string | null } | null;
  /** When the CRON sends the renewal proforma (7 days before Y) — shown until it is issued. */
  proformaExpectedAt?: string;
  preferenceBlockedReason?: PreferenceBlockedReason | null;
  /**
   * AC-16 (review H-9): false when the feature flags are off — `renewal-options` and the
   * renewal-preference endpoints then return 404, so no "Zmień pakiet" CTA may be offered.
   * Always sent by the backend; optional only for pre-existing fixtures/older payloads.
   */
  renewalPreferenceAvailable?: boolean;
}

/**
 * Get the enriched renewal preview backing `SubscriptionTimeline` (AC-11/AC-14).
 * Same endpoint as `getRenewalPreview`, typed for the additional fields the timeline needs.
 *
 * @throws Error if request fails or user is not authenticated
 */
export async function getRenewalTimeline(): Promise<RenewalTimelinePreview> {
  try {
    const response = await apiClient.get<RenewalTimelinePreview>(
      "/billing/self-service/renewal-preview"
    );
    return response.data;
  } catch (error: any) {
    throw new Error(
      `Błąd pobierania danych o kolejnym okresie: ${error.response?.statusText || error.message}`
    );
  }
}

/** One product × interval cell of the AC-11 `renewal-options` matrix. */
export interface RenewalOptionEntry {
  productId: string;
  displayName: string;
  billingInterval: BillingInterval;
  price: RenewalPreviewPrice | null;
  priceUnavailableReason?: PriceUnavailableReason | null;
  /** AC-4 selectability policy result — false disables this cell in the modal. */
  selectable: boolean;
  /**
   * Modules of the package (its catalog entry's `metadata.modules`), e.g.
   * `["grants:kfs", "grants:bur"]` — for the modal's "Utracisz: …" comparison (review M-1).
   * Deliberately not the whole catalog metadata: its `priceMonthly` must never be shown
   * as a renewal price (AC-15).
   */
  modules: string[];
}

export interface RenewalOptionsResponse {
  options: RenewalOptionEntry[];
}

/**
 * Typed error thrown when GET renewal-options returns 404 — the feature is disabled
 * (flag off) rather than unreachable. Lets `RenewalPackageModal` show a non-actionable
 * "niedostępne" message instead of an endless "Spróbuj ponownie" retry loop.
 */
export class RenewalPreferenceUnavailableError extends Error {
  readonly code = "RENEWAL_PREFERENCE_UNAVAILABLE";
  constructor() {
    super("Zmiana pakietu na kolejny okres jest chwilowo niedostępna");
    this.name = "RenewalPreferenceUnavailableError";
  }
}

/**
 * Get the product × interval price/selectability matrix for the AC-15 package modal.
 * `GET /billing/self-service/renewal-options` (AC-11, Tier 1).
 *
 * @throws RenewalPreferenceUnavailableError on 404 (feature flag off)
 * @throws Error if request fails
 */
export async function getRenewalOptions(): Promise<RenewalOptionsResponse> {
  try {
    const response = await apiClient.get<RenewalOptionsResponse>(
      "/billing/self-service/renewal-options"
    );
    return response.data;
  } catch (error: any) {
    if (error.response?.status === 404) {
      throw new RenewalPreferenceUnavailableError();
    }
    throw new Error(
      `Błąd pobierania opcji pakietów: ${error.response?.statusText || error.message}`
    );
  }
}

/** Result of a successful PUT/DELETE renewal-preference call (AC-7: 202 when the proforma swap exceeds the request deadline). */
export interface RenewalPreferenceResult {
  productId: string;
  billingInterval: BillingInterval;
  /** True on HTTP 202 — the proforma swap didn't finish in time and completes via cron retry (AC-7). */
  proformaPending: boolean;
}

/**
 * Typed error thrown when PUT/DELETE renewal-preference returns 409 Conflict.
 * `errorCode` is one of the closed `PreferenceBlockedReason` list (AC-5), mapped to a
 * Polish message by the caller (same pattern as `error-code-mapper.ts`).
 */
export class RenewalPreferenceConflictError extends AccountServiceConflictError {
  readonly code = "RENEWAL_PREFERENCE_CONFLICT";
  constructor(readonly errorCode: string | null = null) {
    super("Nie można teraz zmienić pakietu na kolejny okres");
    this.name = "RenewalPreferenceConflictError";
  }
}

function parseRenewalPreferenceResponse(
  status: number,
  data: { productId: string; billingInterval: BillingInterval }
): RenewalPreferenceResult {
  return {
    productId: data.productId,
    billingInterval: data.billingInterval,
    proformaPending: status === 202,
  };
}

/**
 * Set the renewal preference (package + interval) for the next period (AC-11).
 * `PUT /billing/self-service/renewal-preference { productId, billingInterval }`.
 *
 * @throws RenewalPreferenceConflictError on 409 (AC-5 blocked reasons)
 * @throws Error if request fails
 */
export async function setRenewalPreference(
  productId: string,
  billingInterval: BillingInterval
): Promise<RenewalPreferenceResult> {
  try {
    const response = await apiClient.put<{ productId: string; billingInterval: BillingInterval }>(
      "/billing/self-service/renewal-preference",
      { productId, billingInterval }
    );
    return parseRenewalPreferenceResponse(response.status, response.data);
  } catch (error: any) {
    if (error.response?.status === 409) {
      throw new RenewalPreferenceConflictError(error.response?.data?.errorCode ?? null);
    }
    throw new Error(
      `Błąd zapisu preferencji odnowienia: ${error.response?.statusText || error.message}`
    );
  }
}

/**
 * Reset the renewal preference to the default target (AC-11: "Zostań przy obecnym pakiecie").
 * `DELETE /billing/self-service/renewal-preference`.
 *
 * @throws RenewalPreferenceConflictError on 409 (AC-5 blocked reasons)
 * @throws Error if request fails
 */
export async function clearRenewalPreference(): Promise<RenewalPreferenceResult> {
  try {
    const response = await apiClient.delete<{ productId: string; billingInterval: BillingInterval }>(
      "/billing/self-service/renewal-preference"
    );
    return parseRenewalPreferenceResponse(response.status, response.data);
  } catch (error: any) {
    if (error.response?.status === 409) {
      throw new RenewalPreferenceConflictError(error.response?.data?.errorCode ?? null);
    }
    throw new Error(
      `Błąd resetowania preferencji odnowienia: ${error.response?.statusText || error.message}`
    );
  }
}
